// lib/fetchTagData.ts
import { CategoryInfo, CategoryProductsResponse, Product, Brand } from '@/lib/types';

// Функция для кодирования ID продукта
const encodeProductId = (id: string | number): string => {
    return Buffer.from(`post:${id}`).toString('base64');
};

// Получение информации о метке через WooCommerce REST API
export async function fetchTagDetails(slug: string): Promise<CategoryInfo | null> {
    const url = `https://retail.vitaline.uz/wp-json/wc/v3/products/tags?slug=${slug}`;
    const response = await fetch(url, {
        headers: {
            Authorization: `Basic ${Buffer.from(
                `${process.env.WOOCOMMERCE_CONSUMER_KEY}:${process.env.WOOCOMMERCE_CONSUMER_SECRET}`
            ).toString('base64')}`,
        },
        next: { revalidate: 60 },
    });

    if (!response.ok) {
        console.error('Failed to fetch tag details:', response.statusText);
        return null;
    }

    const data = await response.json();
    const tag = data.length > 0 ? data[0] : null;
    if (!tag) return null;

    return {
        id: tag.id,
        name: tag.name,
        slug: tag.slug,
        description: tag.description,
        image: undefined, // У меток в WooCommerce нет изображения по умолчанию, оставляем undefined
    };
}

// Получение продуктов и брендов по метке
export async function fetchTagProducts(
    tagSlug: string,
    page: number,
    perPage: number,
    brandSlug: string = ''
): Promise<CategoryProductsResponse> {
    const baseUrl = `https://retail.vitaline.uz/wp-json/vitaline/v1/category-products`;
    const url = new URL(baseUrl);
    url.searchParams.append('tag', tagSlug); // Используем 'tag' вместо 'category'
    url.searchParams.append('page', page.toString());
    url.searchParams.append('per_page', perPage.toString());
    if (brandSlug) {
        url.searchParams.append('brand', brandSlug);
    }

    const response = await fetch(url.toString(), {
        next: { revalidate: 60 },
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch tag products: ${response.statusText}`);
    }

    const data = await response.json();
    const total = parseInt(response.headers.get('X-WP-Total') || '0', 10);
    const totalPages = parseInt(response.headers.get('X-WP-TotalPages') || '0', 10);

    return {
        products: data.products.map((p: any) => ({
            id: encodeProductId(p.id), // Оставляем кодирование ID
            name: p.name,
            slug: p.slug,
            sku: p.sku,
            price: p.price,
            stockQuantity: p.stock_quantity,
            stockStatus: p.stock_status.toUpperCase(),
            image: p.images[0]
                ? {
                    sourceUrl: p.images[0].src,
                    altText: p.images[0].alt,
                }
                : undefined,
            brands: {
                nodes: p.brands.map((b: any) => ({
                    id: b.id,
                    name: b.name,
                    slug: b.slug,
                })),
            },
            customSalePrice: p.custom_sale_price || null,
            hasCustomDiscount: p.has_custom_discount || false,
        })),
        brands: data.brands,
        total,
        totalPages,
    };
}